import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Activity, AlertTriangle, CheckCircle2, Link2, Loader2, Plug, Unplug } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * Outlook / Microsoft 365 integration card (Phase 1 — OAuth wire-up).
 *
 * A self-contained sibling of the Slack card on the Integrations page: same
 * three states (not configured / not connected / connected), same connect flow
 * (full navigation to the Microsoft consent screen), same callback handling
 * (reads ?outlook= off the URL once, then strips it).
 */

interface OutlookInstallation {
  id: number;
  connected_user_email: string | null;
  connected_user_name: string | null;
  ms_tenant: string | null;
  scopes: string[];
  installed_at: string;
  installed_by_name: string | null;
  active: boolean;
}

interface OutlookStatus {
  configured: boolean;
  missing_config: string[];
  connected: boolean;
  installation: OutlookInstallation | null;
  requested_scopes: string[];
  redirect_uri: string | null;
}

interface OutlookIdentity {
  user: { name: string | null; email: string | null } | null;
}

const CALLBACK_MESSAGES: Record<string, string> = {
  INVALID_STATE: 'That install link was not valid. Start the connection again.',
  EXPIRED_STATE: 'That install link expired. Start the connection again.',
  MISSING_PARAMS: 'Microsoft did not send back everything needed to finish. Try again.',
  NOT_PERMITTED: 'External integrations are not enabled for this organization.',
  INCOMPLETE_OAUTH_RESPONSE: 'Microsoft did not return an access token. Try connecting again.',
  NO_REFRESH_TOKEN:
    'Microsoft did not return a refresh token. Remove Jarvis from your Microsoft account permissions and connect again.',
  access_denied: 'The install was cancelled at Microsoft.',
  invalid_request:
    'Microsoft rejected the Outlook connection request. Check the Azure app: redirect URI must be a Web platform URI (not SPA), matching the value shown on this card.',
};

const SCOPE_PURPOSE: Record<string, string> = {
  // Granted by older connections only: Jarvis no longer requests it and never sends mail.
  'Mail.Send': 'Send email (granted earlier, no longer requested; Jarvis never sends mail)',
  'Mail.Read': 'Read your mail',
  'User.Read': 'Identify the connected mailbox',
  offline_access: 'Stay connected without re-signing in each hour',
  openid: 'Sign you in',
  profile: 'Read your basic profile',
  email: 'Read your email address',
};

const OutlookCard: React.FC = () => {
  const [status, setStatus] = useState<OutlookStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [checking, setChecking] = useState(false);
  const [identity, setIdentity] = useState<OutlookIdentity | null>(null);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/integrations/outlook/status');
      setStatus(res.data);
    } catch {
      toast.error('Could not load the Outlook connection status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const outcome = searchParams.get('outlook');
    if (!outcome) return;

    const reason = searchParams.get('reason');
    const detail = searchParams.get('detail');
    if (outcome === 'connected') toast.success('Outlook connected.');
    else if (outcome === 'cancelled') {
      const base = CALLBACK_MESSAGES[reason || ''] || 'Outlook connection cancelled.';
      toast.error(detail ? `${base} (${detail})` : base, { duration: detail ? 12000 : 4000 });
    } else toast.error(CALLBACK_MESSAGES[reason || ''] || 'Could not connect Outlook.');

    searchParams.delete('outlook');
    searchParams.delete('reason');
    searchParams.delete('detail');
    setSearchParams(searchParams, { replace: true });
    void load();
  }, [searchParams, setSearchParams, load]);

  const handleConnect = async () => {
    setWorking(true);
    try {
      const res = await api.post('/api/integrations/outlook/install', {});
      window.location.href = res.data.authorize_url;
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not start the Outlook connection.');
      setWorking(false);
    }
  };

  // Reads the connected mailbox owner through the live token, which also proves the
  // connection still works end to end (refreshing the access token if it has expired).
  const handleTestConnection = async () => {
    setChecking(true);
    setNeedsReconnect(false);
    try {
      const res = await api.get('/api/integrations/outlook/whoami');
      setIdentity(res.data);
      toast.success('Outlook connection is working.');
      await load();
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.code === 'REAUTH_REQUIRED') {
        setNeedsReconnect(true);
        setIdentity(null);
      } else {
        toast.error(data?.error || 'Could not reach Microsoft.');
      }
    } finally {
      setChecking(false);
    }
  };

  const handleDisconnect = async () => {
    setWorking(true);
    try {
      await api.delete('/api/integrations/outlook');
      toast.success('Outlook disconnected.');
      setConfirmDisconnect(false);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not disconnect Outlook.');
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <section className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </section>
    );
  }

  const install = status?.installation;

  return (
    <section className="bg-white border border-gray-200 rounded-xl p-5" data-testid="outlook-integration-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0">
            <Plug className="w-5 h-5 text-gray-500" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900">Outlook — org mailbox (admin)</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Optional shared mailbox for admin tooling. Personal calendars and drafts for BD
              Assistant are each person&apos;s own connection under Settings → BD Assistant — not this card.
            </p>
          </div>
        </div>

        {status?.connected ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium flex-shrink-0">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Connected
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium flex-shrink-0">
            Not connected
          </span>
        )}
      </div>

      {!status?.configured && (
        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 min-w-0">
              <p className="font-medium">Outlook is not configured on this server yet.</p>
              <p className="mt-1">
                An Azure app registration (Microsoft Entra) has to be created and its credentials set
                as environment variables before anyone can connect.
              </p>
              {status?.missing_config?.length ? (
                <ul className="mt-2 space-y-0.5 font-mono text-[11px]">
                  {status.missing_config.map((key) => (
                    <li key={key}>· {key}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {needsReconnect && (
        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="outlook-reconnect-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900">
              Microsoft no longer accepts this connection. Disconnect it and connect Outlook again to keep reading mail.
            </p>
          </div>
        </div>
      )}

      {status?.connected && install && (
        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
          <div className="col-span-2 sm:col-span-1">
            <dt className="text-gray-500">Org mailbox</dt>
            <dd className="text-gray-900 font-medium mt-0.5 truncate">
              <span data-testid="outlook-mailbox">
                {identity?.user?.email || install.connected_user_email || identity?.user?.name || install.connected_user_name || 'Connected'}
              </span>
            </dd>
            <p className="text-[11px] text-gray-500 mt-1">
              Shared admin mailbox only. Each person connects their own Outlook in Settings → BD Assistant.
            </p>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <dt className="text-gray-500">Connected by</dt>
            <dd className="text-gray-900 font-medium mt-0.5 truncate">
              {install.installed_by_name || 'Unknown'}
              {install.installed_at && (
                <span className="text-gray-500 font-normal">
                  {' · '}
                  {new Date(install.installed_at).toLocaleDateString()}
                </span>
              )}
            </dd>
          </div>
          <div className="col-span-2">
            <dt className="text-gray-500 mb-1">What Jarvis can do with this mailbox</dt>
            <dd>
              <ul className="space-y-1">
                {install.scopes.map((scope) => (
                  <li key={scope} className="text-gray-700">
                    <span className="font-mono text-[11px] text-gray-500">{scope}</span>
                    {SCOPE_PURPOSE[scope] && <span> — {SCOPE_PURPOSE[scope]}</span>}
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        </dl>
      )}

      {status?.configured && !status?.connected && status.redirect_uri && (
        <p className="mt-4 text-[11px] text-gray-500">
          <Link2 className="w-3 h-3 inline mr-1" />
          The Azure app's redirect URI must be exactly{' '}
          <span className="font-mono text-gray-700 break-all">{status.redirect_uri}</span>
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        {status?.connected ? (
          confirmDisconnect ? (
            <>
              <span className="text-xs text-gray-600 self-center mr-auto">
                Disconnect Outlook? Jarvis stops reading mail immediately.
              </span>
              <button
                onClick={() => setConfirmDisconnect(false)}
                disabled={working}
                className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDisconnect}
                disabled={working}
                data-testid="outlook-disconnect-confirm"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {working ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unplug className="w-3.5 h-3.5" />}
                Disconnect
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleTestConnection}
                disabled={checking}
                data-testid="outlook-test-connection"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />}
                Test connection
              </button>
              <button
                onClick={() => setConfirmDisconnect(true)}
                data-testid="outlook-disconnect"
                className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Disconnect
              </button>
            </>
          )
        ) : (
          <button
            onClick={handleConnect}
            disabled={!status?.configured || working}
            data-testid="outlook-connect"
            title={status?.configured ? undefined : 'Outlook is not configured on this server yet'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {working ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />}
            Connect Outlook
          </button>
        )}
      </div>
    </section>
  );
};

export default OutlookCard;
